import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  portalMessagesQueryKey,
  usePortalMessages,
  useSendPortalMessage,
  useMarkPortalMessageRead,
  type PortalMessage,
} from '@/hooks/usePortalMessages';
import { useCurrentPatient } from '@/hooks/useCurrentPatient';
import { personDisplayName, resolveMessageSender } from '@/utils/messageSender';
import { validateMessageAttachmentFile, useMessageAttachments } from '@/hooks/useMessageAttachments';
import { useGetDocumentDownloadUrl } from '@/hooks/useDocuments';
import { supabase } from '@/lib/supabase';
import { usePortalActiveClinic } from '@/lib/portalActiveClinic';
import {
  portalMessageGateway,
  sendPortalComposerMessage,
  type PortalMessageGateway,
} from '@/lib/portalMessageSend';
import { isUnreadClinicMessage, patientInitials } from '@/components/portal/dashboard/portalDashboardUtils';
import type { PortalClinicItem } from '@/hooks/usePortalClinics';
import { PortalPageHead } from '@/components/portal/PortalChrome';
import { PortalIcon } from '@/components/portal/PortalIcon';
import { formatPortalDateTime, formatPortalFileSize, formatPortalShortDate } from '@/lib/portalFormat';
import { clockFromIso } from '@/lib/portalFormat';

export function PortalMessages() {
  const { activeClinicId, clinics } = usePortalActiveClinic();
  const activeClinic = clinics.find((clinic) => clinic.clinic_id === activeClinicId);
  const { data: patient } = useCurrentPatient();
  const patientName = personDisplayName(patient?.first_name, patient?.last_name);
  const queryClient = useQueryClient();
  const { data: messages, isLoading, error } = usePortalMessages(activeClinicId);
  const sendMessage = useSendPortalMessage();
  const markRead = useMarkPortalMessageRead();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [content, setContent] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);

  const unread = (messages ?? []).filter(isUnreadClinicMessage);
  useEffect(() => {
    if (!activeClinicId || unread.length === 0) return;
    unread.forEach((message) => {
      void markRead.mutateAsync({ message_id: message.id, clinic_id: activeClinicId }).catch(() => undefined);
    });
  }, [activeClinicId, unread.map((row) => row.id).join(',')]);

  const latestClinic = [...(messages ?? [])].reverse().find((row) => row.sender === 'clinic');

  const handleSend = async () => {
    if (!activeClinicId) {
      toast.error('Ingen klinik valgt');
      return;
    }
    if (!content.trim() && files.length === 0) {
      toast.error('Besked eller fil påkrævet');
      return;
    }
    setUploading(true);
    try {
      await sendPortalComposerMessage(
        {
          clinicId: activeClinicId,
          patientId: patient?.id ?? null,
          content,
          files,
        },
        {
          ...portalMessageGateway(supabase as unknown as PortalMessageGateway),
          sendText: async (clinicId, text) => {
            const sent = await sendMessage.mutateAsync({ clinic_id: clinicId, content: text });
            return sent as { id: string };
          },
        },
      );
      await queryClient.invalidateQueries({ queryKey: portalMessagesQueryKey(activeClinicId) });
      toast.success('Besked sendt');
      setContent('');
      setFiles([]);
    } catch (err) {
      console.error('Error sending message:', err);
      toast.error('Kunne ikke sende besked');
    } finally {
      setUploading(false);
    }
  };

  const handleFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next: File[] = [];
    for (const file of Array.from(event.target.files ?? [])) {
      const validation = validateMessageAttachmentFile(file);
      if (validation === 'size_limit_exceeded') toast.error(`Filen ${file.name} er for stor (max 50 MB)`);
      else if (validation === 'invalid_type') toast.error(`Filtypen for ${file.name} er ikke tilladt`);
      else next.push(file);
    }
    setFiles((prev) => [...prev, ...next]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <>
      <PortalPageHead
        title="Beskeder"
        subtitle={`Skriv til ${activeClinic?.clinic_name ?? 'klinikken'}.`}
      />
      {isLoading ? (
        <section className="card">
          <p className="help">Henter beskeder…</p>
        </section>
      ) : error ? (
        <section className="card">
          <p className="help">Beskederne kunne ikke hentes. Prøv igen.</p>
        </section>
      ) : (
        <section className="card msg-layout">
          <div className="threads">
            <div className="h">
              <b>Samtaler</b>
            </div>
            <div className="th on">
              <ClinicThreadAvatar clinic={activeClinic} />
              <div className="grow">
                <div className="n">
                  {latestClinic?.sender_name ?? activeClinic?.clinic_name ?? 'Klinikken'}
                  {unread.length > 0 ? <span className="dot" aria-label="Ulæst" /> : null}
                  <span className="d">
                    {messages?.[0] ? formatPortalShortDate(messages[messages.length - 1]?.created_at) : ''}
                  </span>
                </div>
                <div className="p">{messages?.[messages.length - 1]?.content ?? 'Ingen beskeder endnu'}</div>
              </div>
            </div>
          </div>
          <div className="conv">
            <div className="h">
              <ClinicThreadAvatar clinic={activeClinic} />
              <div>
                <b>{latestClinic?.sender_name ?? activeClinic?.clinic_name ?? 'Klinikken'}</b>
                <span>{activeClinic?.clinic_name ?? 'Patientportal'}</span>
              </div>
            </div>
            <div className="bubbles">
              {(messages ?? []).length === 0 ? (
                <p className="help">Send en besked til klinikken for at starte.</p>
              ) : (
                <MessageBubbles messages={messages ?? []} patientName={patientName} />
              )}
            </div>
            <div className="composer">
              <div className="field">
                <label htmlFor="msg" className="sr">
                  Skriv din besked
                </label>
                <textarea
                  id="msg"
                  className="input"
                  style={{ minHeight: 72 }}
                  placeholder="Skriv din besked her …"
                  value={content}
                  onChange={(event) => setContent(event.target.value)}
                  disabled={uploading}
                />
              </div>
              {files.length > 0 ? (
                <div>
                  {files.map((file, index) => (
                    <div key={`${file.name}-${index}`} className="att" style={{ marginBottom: 8 }}>
                      <span className="ft">FIL</span>
                      {file.name}
                      <span className="muted" style={{ fontSize: 13 }}>
                        {formatPortalFileSize(file.size)}
                      </span>
                      <button type="button" className="btn btn-tertiary btn-icon" onClick={() => setFiles((prev) => prev.filter((_, i) => i !== index))}>
                        <PortalIcon name="x" size="sm" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}
              <div className="bar">
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.txt"
                  onChange={handleFiles}
                />
                <button type="button" className="btn btn-tertiary" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                  <PortalIcon name="paperclip" size="sm" />
                  Vedhæft fil
                </button>
                <span className="help d-only">Du kan vedhæfte filer, fx PDF eller billeder</span>
                <span className="grow" />
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => void handleSend()}
                  disabled={uploading || (!content.trim() && files.length === 0)}
                >
                  <PortalIcon name="send" size="sm" />
                  {uploading ? 'Sender…' : 'Send'}
                </button>
              </div>
            </div>
          </div>
        </section>
      )}
    </>
  );
}

function MessageBubbles({
  messages,
  patientName,
}: {
  messages: PortalMessage[];
  patientName: string;
}) {
  const days = useMemo(() => {
    const groups: Array<{ day: string; items: PortalMessage[] }> = [];
    for (const message of messages) {
      const day = formatPortalDateTime(message.created_at).split(' · ')[0] ?? '';
      const last = groups[groups.length - 1];
      if (last && last.day === day) last.items.push(message);
      else groups.push({ day, items: [message] });
    }
    return groups;
  }, [messages]);

  return (
    <>
      {days.map((group) => (
        <div key={group.day} style={{ display: 'contents' }}>
          <span className="daysep">{group.day}</span>
          {group.items.map((message) => {
            const mine = message.sender === 'patient';
            const sender = resolveMessageSender(message, { patientName });
            return (
              <div key={message.id} className={`bub${mine ? ' me' : ''}`}>
                <span className="who">
                  {mine ? 'Dig' : sender.name} · {clockFromIso(message.created_at)}
                </span>
                {message.content ? <div className="tx">{message.content}</div> : null}
                <MessageAttachments messageId={message.id} />
              </div>
            );
          })}
        </div>
      ))}
    </>
  );
}

function ClinicThreadAvatar({ clinic }: { clinic: PortalClinicItem | undefined }) {
  const name = clinic?.clinic_name ?? 'Klinikken';
  const parts = name.trim().split(/\s+/);
  const initials = patientInitials(parts[0], parts[1]);
  if (clinic?.clinic_logo) {
    return <img className="avatar av40" src={clinic.clinic_logo} alt="" />;
  }
  return <span className="avatar av40 av-a">{initials}</span>;
}

function MessageAttachments({ messageId }: { messageId: string }) {
  const { data: attachments, isLoading } = useMessageAttachments(messageId);
  const getDownloadUrl = useGetDocumentDownloadUrl();
  if (isLoading || !attachments || attachments.length === 0) return null;

  const download = async (documentId: string, filename: string) => {
    try {
      const url = await getDownloadUrl.mutateAsync(documentId);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error downloading attachment:', err);
      toast.error('Kunne ikke downloade fil');
    }
  };

  return (
    <>
      {attachments.map((attachment) => (
        <button
          key={attachment.id}
          type="button"
          className="att"
          onClick={() => void download(attachment.document_id, attachment.document.original_filename)}
        >
          <span className="ft">FIL</span>
          {attachment.document.original_filename}
          <PortalIcon name="download" size="sm" />
        </button>
      ))}
    </>
  );
}
